<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { iTitleBanner, iSpinner } from '@inscribe/signal';
import { encodeQueryParams, getAndSyncQueryParams } from '@/utils/queryParams';
import DatesFilter from '@/V2/components/filters/DatesFilter';
import TagsFilter from '@/V2/components/filters/TagsFilter';
import DocumentTypesFilter from '@/V2/components/filters/DocumentTypesFilter';
import FileTypesFilter from '@/V2/components/filters/FileTypesFilter';
import CustomerFilter from '@/V2/components/filters/CustomerFilter';
import CustomerStatusesFilter from '@/V2/components/filters/CustomerStatusesFilter';
import ChartCard from '@/V2/analytics/ChartCard';
import { DATA_BRICK_ENTITY_TYPE } from '@/V2/analytics/DataBrick';
import ChartWrapper from '@/V2/analytics/ChartWrapper';
import DocumentRiskLevelsByDay from '@/V2/analytics/DocumentRiskLevelsByDay';
import CustomerRiskLevelBreakdownCard from '@/V2/analytics/CustomerRiskLevelBreakdownCard';
import {
  InscribeAnalyticsBluePalette,
  InscribeAnalyticsRedPalette,
} from './../analytics/utils';
import EmptyPage from '@/shared/components/EmptyPage';

export default {
  components: {
    iSpinner,
    iTitleBanner,
    DatesFilter,
    TagsFilter,
    DocumentTypesFilter,
    FileTypesFilter,
    CustomerStatusesFilter,
    CustomerFilter,
    ChartCard,
    ChartWrapper,
    DocumentRiskLevelsByDay,
    CustomerRiskLevelBreakdownCard,
    EmptyPage,
  },
  data() {
    return {
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      isLoading: false,
      data: undefined,
      error: undefined,
      fetchAbortController: undefined,
      InscribeAnalyticsBluePalette,
      InscribeAnalyticsRedPalette,
      DATA_BRICK_ENTITY_TYPE,
    };
  },
  computed: {
    ...mapGetters([
      'isOrgAdvancedUserOrAdmin',
      'isOrgAdvancedReadOnlyUser',
      'canViewBusinessComplianceAgent',
    ]),
    numberOfCustomers() {
      return this.data?.number_of_customers.toLocaleString() ?? '';
    },
    numberOfDocuments() {
      return this.data?.number_of_documents.toLocaleString() ?? '';
    },
    numberOfHighRiskDocs() {
      return this.data?.number_of_high_risk_documents.toLocaleString() ?? '';
    },
    numberOfHighRiskCustomers() {
      return this.data?.number_of_high_risk_customers.toLocaleString() ?? '';
    },
    formattedNumberOfCustomersWithNoDocuments() {
      return (
        this.data?.number_of_customers_analysed_without_documents.toLocaleString() ??
        ''
      );
    },
    customersWithNoDocumentsRate() {
      if (!this.data) {
        return '';
      }
      if (this.data?.number_of_customers === 0) {
        return '0%';
      }
      const raw =
        this.data?.number_of_customers_analysed_without_documents /
        this.data?.number_of_customers;
      return `${Math.round(raw * 1000) / 10}%`;
    },

    typeOfDocsCount() {
      return this.data?.file_type_breakdown.length;
    },
    trustScoreWithPadding() {
      return Array.from(Array(101).keys()).map((i) => {
        const foundIndex = this.data.trust_score_breakdown.findIndex(
          (d) => d.bucket === i,
        );
        return foundIndex > -1
          ? this.data.trust_score_breakdown[foundIndex]
          : {
              bucket: i,
              count: 0,
            };
      });
    },
    documentRiskLevelsByDay() {
      return this.data.document_risk_levels_by_day;
    },
    highRiskDocsRate() {
      if (!this.data) {
        return '';
      }
      if (this.data.number_of_documents === 0) {
        return '0%';
      }
      const raw =
        this.data.number_of_high_risk_documents / this.data.number_of_documents;
      return `${Math.round(raw * 1000) / 10}%`;
    },
    highRiskCustomerRate() {
      if (!this.data) {
        return '';
      }
      if (this.data.number_of_customers === 0) {
        return '0%';
      }
      const raw =
        this.data.number_of_high_risk_customers / this.data.number_of_customers;
      return `${Math.round(raw * 1000) / 10}%`;
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
    params: {
      handler: 'fetchData',
      immediate: true,
    },
  },
  methods: {
    async fetchData() {
      // if there is a previous inflight request, cancel it
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
      }
      this.fetchAbortController = new AbortController();

      this.isLoading = true;
      try {
        const res = await axios.get(
          `/api/v2/analytics?${encodeQueryParams(this.params)}`,
          {
            signal: this.fetchAbortController.signal,
          },
        );
        this.data = res.data.data;
      } catch (e) {
        if (e.message !== 'canceled') {
          this.error = e;
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="analytics-page__wrapper" data-testid="analytics-page">
    <iTitleBanner
      title="Analytics"
      subtitle="Insights into overall fraud detection and performance"
      icon-name="large-graph-2"
      data-testid="analytics-page-title"
      class-name="analytics-page__title-banner"
    />

    <!-- missing permissions message-->
    <empty-page
      v-if="!isOrgAdvancedUserOrAdmin && !isOrgAdvancedReadOnlyUser"
      icon="large-lock"
      title="Permission Required"
      message="You do not have sufficient permissions to access this page. Please ask your team admin to give you access."
    ></empty-page>

    <div v-else class="analytics-page__content">
      <div
        :key="isLoading"
        :class="{ 'is-loading': isLoading }"
        class="analytics-page__filters"
      >
        <DatesFilter />
        <FileTypesFilter />
        <DocumentTypesFilter />
        <CustomerFilter />
        <CustomerStatusesFilter entity="analytics" />
        <TagsFilter />
        <iSpinner v-if="isLoading" size="sm" type="neutral" />
      </div>

      <div class="analytics-page__metrics">
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">
            High Risk Customers
          </div>
          <h4
            v-if="highRiskCustomerRate"
            data-testid="analytics-page-high-risk-customers-metric"
          >
            {{ numberOfHighRiskCustomers }} of {{ numberOfCustomers }} ({{
              highRiskCustomerRate
            }})
          </h4>
        </div>
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">
            High Risk Documents
          </div>
          <h4
            v-if="highRiskDocsRate"
            data-testid="analytics-page-high-risk-documents-metric"
          >
            {{ numberOfHighRiskDocs }} of {{ numberOfDocuments }} ({{
              highRiskDocsRate
            }})
          </h4>
        </div>
        <div
          v-if="canViewBusinessComplianceAgent"
          class="analytics-page__metric i-radius-2xl"
        >
          <div class="analytics-page__metric-subheading">
            Customers Analysed Without Documents
          </div>

          <h4
            v-if="customersWithNoDocumentsRate"
            data-testid="analytics-page-customers-with-no-documents-metric"
          >
            {{ formattedNumberOfCustomersWithNoDocuments }} of
            {{ numberOfCustomers }} ({{ customersWithNoDocumentsRate }})
          </h4>
        </div>
      </div>
      <div
        v-if="data"
        class="analytics-page__charts d-flex flex-column i-gap-8"
      >
        <div class="analytics-page__chart-row">
          <chart-wrapper
            title="Document Risk Levels"
            subtitle="Breakdown of document risk levels per day"
          >
            <template #chart>
              <document-risk-levels-by-day
                :data="documentRiskLevelsByDay"
              ></document-risk-levels-by-day>
            </template>
          </chart-wrapper>
        </div>

        <div class="analytics-page__chart-row">
          <!-- This chart is not clickable until the Customers page supports filtering by risk level -->
          <customer-risk-level-breakdown-card
            data-testid="analytics-page-customer-risk-level-breakdown-card"
            :data="data.customer_risk_level_breakdown"
          ></customer-risk-level-breakdown-card>
        </div>

        <div class="analytics-page__chart-row">
          <chart-card
            title="High Risk Documents by Type"
            subtitle="Breakdown of high risk workspace documents by type"
            data-testid="analytics-page-high-risk-documents-by-type-chart"
            :data="data.high_risk_document_type_breakdown"
            :total="data.number_of_high_risk_documents"
            :legend-clickable="true"
            :chart-palette="InscribeAnalyticsRedPalette"
            :entity="DATA_BRICK_ENTITY_TYPE.HIGH_RISK_DOCS"
            size="lg"
          ></chart-card>
        </div>

        <div class="analytics-page__chart-row">
          <chart-card
            title="Document Types"
            subtitle="Breakdown of workspace documents by type"
            data-testid="analytics-page-document-types-chart"
            :data="data.document_type_breakdown"
            :total="data.number_of_documents"
            :legend-clickable="true"
            :chart-palette="InscribeAnalyticsBluePalette"
            :entity="DATA_BRICK_ENTITY_TYPE.DOCS"
          ></chart-card>
          <chart-card
            title="File Types"
            subtitle="Breakdown of workspace file formats"
            data-testid="analytics-page-file-types-chart"
            :data="data.file_type_breakdown"
            :total="typeOfDocsCount"
            :legend-clickable="true"
            :chart-palette="InscribeAnalyticsBluePalette"
            :entity="DATA_BRICK_ENTITY_TYPE.FILE_TYPES"
          ></chart-card>
        </div>
        <div class="analytics-page__chart-row">
          <chart-card
            :data="trustScoreWithPadding"
            title="Trust Score Distribution"
            subtitle="Distribution of trust scores across workspace documents"
            :entity="DATA_BRICK_ENTITY_TYPE.TRUST_SCORE"
            size="full"
          ></chart-card>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.analytics-page {
  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: map.get($increments, 11) map.get($increments, 12);
    max-width: $desktop;
    margin: auto;

    @media only screen and (max-width: 880px) {
      padding: map.get($increments, 11) map.get($increments, 7);
      max-width: 100%;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    h1 {
      margin: 0;
    }
  }

  &__charts {
    width: 100%;
  }

  &__chart-row {
    display: flex;
    flex-direction: row;
    gap: map.get($increments, 8);
    width: 100%;

    @include respond-to('medium') {
      flex-direction: column;
    }
  }

  &__filters {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: map.get($increments, 3);
    margin: map.get($increments, 11) 0;

    &.is-loading {
      pointer-events: none;
    }
  }

  &__metrics {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: map.get($increments, 8);
    width: 100%;
    margin-bottom: map.get($increments, 8);

    @include respond-to('small') {
      flex-direction: column;
    }
  }

  &__metric {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    gap: map.get($increments, 2);
    padding: map.get($increments, 9);
    border: 1px solid $light-neutral-60;
    min-height: 93px;

    @include respond-to('medium') {
      min-height: 111px;
    }

    @include respond-to('small') {
      min-height: 93px;
    }

    h4 {
      margin-bottom: 0;
    }
  }

  &__metric-subheading {
    color: $light-neutral-110;
    font-size: map.get($text-size-standard, 3);
    font-weight: map.get($text-weights, 'medium');
  }
}
</style>
